/**
 * ActivityLoader - Fetches, validates, and resolves activity JSON definitions.
 *
 * Handles loading activity data from the curriculum file structure,
 * validating against the activity schema, and resolving asset paths.
 *
 * @see GitHub Issue #852
 */

import {validateActivity, validateManifest} from './validateActivity';

const ACTIVITIES_BASE = '/activities';
const CACHE = new Map();

export default class ActivityLoader {
    /**
     * Load a curriculum manifest (top-level or course-level).
     * @param {string} [manifestPath] - Path relative to activities root. Default: manifest.json
     * @returns {Promise<{valid: boolean, data?: object, errors?: string[]}>}
     */
    static async loadManifest (manifestPath) {
        const path = manifestPath || `${ACTIVITIES_BASE}/manifest.json`;
        try {
            const data = await ActivityLoader._fetch(path);
            const validation = validateManifest(data);
            if (!validation.valid) {
                console.error('[ActivityLoader] Invalid manifest:', validation.errors);
                return {valid: false, errors: validation.errors};
            }
            return {valid: true, data};
        } catch (err) {
            console.error('[ActivityLoader] Failed to load manifest:', err);
            return {valid: false, errors: [err.message]};
        }
    }

    /**
     * Load the top-level curriculum manifest and resolve course manifests.
     * @returns {Promise<{valid: boolean, data?: object, errors?: string[]}>}
     */
    static async loadResolvedCurriculum () {
        const topLevelResult = await ActivityLoader.loadManifest();
        if (!topLevelResult.valid) {
            return topLevelResult;
        }

        const topLevel = topLevelResult.data || {};
        const courseRefs = topLevel.courses || [];
        const resolvedCourses = [];
        const errors = [];

        for (const courseRef of courseRefs) {
            const manifestPath = courseRef.manifestPath
                ? `/activities/${String(courseRef.manifestPath).replace(/^\/+/, '')}`
                : `/activities/courses/${courseRef.courseId}/manifest.json`;
            const courseResult = await ActivityLoader.loadManifest(manifestPath);
            if (!courseResult.valid) {
                errors.push(`${courseRef.courseId}: ${(courseResult.errors || []).join(', ')}`);
                continue;
            }
            resolvedCourses.push({
                ...courseRef,
                ...courseResult.data,
                manifestPath: courseRef.manifestPath
            });
        }

        if (resolvedCourses.length === 0) {
            return {
                valid: false,
                errors: errors.length > 0 ? errors : ['No activity courses available']
            };
        }

        return {
            valid: true,
            data: {
                ...topLevel,
                courses: resolvedCourses
            },
            errors
        };
    }

    /**
     * Load a single activity definition by its ID.
     * @param {string} activityPath - Path to activity JSON relative to activities root
     * @returns {Promise<{valid: boolean, data?: object, errors?: string[]}>}
     */
    static async loadActivity (activityPath) {
        const fullPath = activityPath.startsWith('/') ? activityPath : `${ACTIVITIES_BASE}/${activityPath}`;
        try {
            const data = await ActivityLoader._fetch(fullPath);
            const validation = validateActivity(data);
            if (!validation.valid) {
                console.error('[ActivityLoader] Invalid activity:', validation.errors);
                return {valid: false, errors: validation.errors};
            }
            // Resolve asset paths relative to the activity file location
            const basePath = fullPath.substring(0, fullPath.lastIndexOf('/'));
            ActivityLoader._resolveAssetPaths(data, basePath);
            return {valid: true, data};
        } catch (err) {
            console.error('[ActivityLoader] Failed to load activity:', err);
            return {valid: false, errors: [err.message]};
        }
    }

    /**
     * Load a course manifest and all its activity definitions.
     * @param {string} courseId
     * @returns {Promise<{valid: boolean, course?: object, activities?: object[], errors?: string[]}>}
     */
    static async loadCourse (courseId) {
        const coursePath = `${ACTIVITIES_BASE}/courses/${courseId}/manifest.json`;
        const manifestResult = await ActivityLoader.loadManifest(coursePath);
        if (!manifestResult.valid) {
            return {valid: false, errors: manifestResult.errors};
        }

        const course = manifestResult.data;
        const activities = [];
        const errors = [];

        for (const unit of (course.units || [])) {
            for (const lesson of (unit.lessons || [])) {
                const activityPath = `${ACTIVITIES_BASE}/courses/${courseId}/units/${unit.unitId}/${lesson.file}`;
                const result = await ActivityLoader.loadActivity(activityPath);
                if (result.valid) {
                    activities.push(result.data);
                } else {
                    errors.push(`${lesson.file}: ${result.errors.join(', ')}`);
                }
            }
        }

        return {valid: errors.length === 0, course, activities, errors};
    }

    /**
     * Load starter project template for an activity.
     * @param {string} templatePath
     * @returns {Promise<string|null>}
     */
    static async loadTemplate (templatePath) {
        try {
            const resp = await fetch(ActivityLoader._withBuildVersion(templatePath), {cache: 'no-store'});
            if (!resp.ok) return null;
            return await resp.text();
        } catch (err) {
            console.error('[ActivityLoader] Failed to load template:', err);
            return null;
        }
    }

    /**
     * Clear the fetch cache.
     */
    static clearCache () {
        CACHE.clear();
    }

    /**
     * Fetch JSON with caching.
     * @private
     */
    static async _fetch (path) {
        if (CACHE.has(path)) {
            return CACHE.get(path);
        }
        const resp = await fetch(ActivityLoader._withBuildVersion(path), {cache: 'no-store'});
        if (!resp.ok) {
            throw new Error(`HTTP ${resp.status}: ${path}`);
        }
        const data = await resp.json();
        CACHE.set(path, data);
        return data;
    }

    static _withBuildVersion (path) {
        const buildVersion = typeof window !== 'undefined' && window.__BUILD_VERSION__
            ? String(window.__BUILD_VERSION__)
            : '';
        if (!buildVersion || typeof path !== 'string' || /^https?:\/\//i.test(path)) {
            return path;
        }
        return `${path}${path.includes('?') ? '&' : '?'}v=${encodeURIComponent(buildVersion)}`;
    }

    /**
     * Resolve relative asset paths to absolute paths.
     * @private
     */
    static _resolveAssetPaths (activity, basePath) {
        if (!activity.assets) return;

        const resolve = (arr) => {
            if (!Array.isArray(arr)) return arr;
            return arr.map(p => {
                if (p.startsWith('/') || p.startsWith('http')) return p;
                return `${basePath}/assets/${p}`;
            });
        };

        if (activity.assets.backgrounds) {
            activity.assets.backgrounds = resolve(activity.assets.backgrounds);
        }
        if (activity.assets.sprites) {
            activity.assets.sprites = resolve(activity.assets.sprites);
        }
        if (activity.assets.sounds) {
            activity.assets.sounds = resolve(activity.assets.sounds);
        }
    }
}
